import { Button } from "@shopify/polaris"
import { observer } from "mobx-react"
import { useMemo, useState } from "react"
import { useHistory } from "react-router"

import APIPlan from "@/api/plan"
import { PModal } from "@/components/ConfirmModal/ConfirmModalInPage"
import stores from "@/stores"
import { isUpgrade } from "@/utils/global"
import { openCustomURL } from "@/utils/location"
import { hasChosenPlan } from "@/utils/plan"

interface IPlanButton {
  current: any
  kind: number
  packageName: string
  isSubscribeGuide: boolean
  cioInfo: { plan_monthly_price: number }
  compact?: boolean
}

const PlanButton = (props: IPlanButton) => {
  const { current, kind, packageName, isSubscribeGuide, cioInfo, compact } =
    props
  const isSubscribeBundle = stores.PlanStore.config?.user_pkg?.source === 3
  const [loading, setLoading] = useState(false)

  // console.log("999-current", current)
  const { PlanStore, CommonStore } = stores
  const { type, config = {}, curPackageInfo } = stores.PlanStore
  const { user_pkg = {}, delay = {} } = config
  const isOfficialSubscribe = user_pkg?.source === 2
  const history = useHistory()

  const updatePlan = async () => {
    setLoading(true)
    // 请求api
    const res = await APIPlan.createStorePaidSubscribe(current?.id ?? 1, kind)

    // 请求成功
    if (res.code === 1000) {
      if (kind === 1) {
        await Promise.all([
          stores.UserInfoStore.updateUserInfo(),
          stores.CommonStore.updateAuthority(),
          stores.PlanStore.updatePlanData(),
        ])
      } else {
        // 跳转到订阅套餐页面
        openCustomURL(res?.data?.confirmation_url ?? "", false)
      }
    }
    setLoading(false)
  }

  const handleResumePlan = async () => {
    setLoading(true)
    const res = await APIPlan.resumePlan()
    if (res.code === 1000) {
      if (res?.data?.confirmation_url) {
        openCustomURL(res.data.confirmation_url, false)
      } else {
        await Promise.all([
          stores.UserInfoStore.updateUserInfo(),
          stores.CommonStore.updateAuthority(),
          stores.PlanStore.updatePlanData(),
        ])
      }
    }
    setLoading(false)
  }

  const isUpgradePlan = useMemo(() => {
    if (isSubscribeBundle) {
      return false
    }
    return isUpgrade(curPackageInfo, current)
  }, [curPackageInfo, current, isSubscribeBundle])

  // 点击支付事件
  const handPayMend = async () => {
    // 判断如果是官网订阅套餐，禁用和toast提示
    if (isOfficialSubscribe) {
      PModal.showCWPlanModal()
      return
    }
    if (canResumeCurrentPlan) {
      await handleResumePlan()
      return
    }
    // 触发上报逻辑（免费套餐不上报）
    if (kind !== 1) {
      window.cio?.trigger({
        event: "change_plan",
        payload: {
          plan_type: type === 1 ? "monthly" : "annually",
          plan_version: packageName?.toLowerCase(),
          plan_quota: current?.quota,
          ...cioInfo,
        },
      })
      window.cwreport?.report("change_plan", {
        plan_type: type === 1 ? "monthly" : "annually",
        plan_version: packageName?.toLowerCase(),
        plan_quota: current?.quota,
        ...cioInfo,
      })
    }

    // 如果订阅了bundle套餐
    if (isSubscribeBundle) {
      PModal.showBundlePlanModal({
        onConfirm: async () => {
          PlanStore.setAppType("bundle")
          PlanStore.setBundlePeriod(PlanStore.type)
          PlanStore.setPerferPlanId(current?.id)
          PlanStore.setPerferKind(kind)
        },
      })
      return
    }

    // 判断是否降级
    const isDowngrade = !isUpgradePlan && curPackageInfo?.id !== current?.id
    const isFirstTimeSelectingFree = kind === 1 && !hasChosenPlan()

    if (isDowngrade || isFirstTimeSelectingFree) {
      PModal.showDowngradeModal({
        package_kind: kind,
        onConfirm: async () => {
          await updatePlan()
        },
      })
      return
    }
    updatePlan()
  }

  const isNextPlan = useMemo(() => {
    if (isSubscribeBundle) {
      return false
    }
    // 判断是否有延迟套餐（大于1，表示订阅的延迟生效套餐，等于1，表示免费的延迟生效套餐）
    return delay?.package_id === current?.id
  }, [delay, current, isSubscribeBundle])

  const hasChosenPlanFlag = hasChosenPlan()

  const isCurrentPlan = useMemo(() => {
    if (isSubscribeBundle) {
      return false
    }
    return user_pkg?.package_id === current?.id
  }, [user_pkg, current, isSubscribeBundle])

  const isActiveCurrentPlan = isCurrentPlan && hasChosenPlanFlag

  const hasPendingDowngrade = useMemo(() => {
    if (isSubscribeBundle) {
      return false
    }
    const delayPackageId = delay?.package_id
    if (!delayPackageId || delayPackageId < 1) {
      return false
    }
    return delayPackageId !== user_pkg?.package_id
  }, [delay, user_pkg, isSubscribeBundle])

  const canResumeCurrentPlan = useMemo(() => {
    return isActiveCurrentPlan && hasPendingDowngrade
  }, [isActiveCurrentPlan, hasPendingDowngrade])

  const btnDisabled = useMemo(() => {
    if (isSubscribeGuide) return false
    if (canResumeCurrentPlan) return false
    if (isNextPlan || isActiveCurrentPlan) {
      return true
    }
    if (isOfficialSubscribe) {
      return true
    }
    return false
  }, [
    isSubscribeGuide,
    canResumeCurrentPlan,
    isNextPlan,
    isActiveCurrentPlan,
    isOfficialSubscribe,
  ])

  const btnText = useMemo(() => {
    if (!isSubscribeGuide) {
      if (canResumeCurrentPlan) {
        return t("ResumeThisPlan")
      }
      if (isNextPlan) {
        return t("NextPlan")
      }
      if (isActiveCurrentPlan) {
        return t("CurrentPlan")
      }
    }
    if (kind === 1 || !hasChosenPlanFlag) {
      return t("ChooseThisPlan")
    }
    if (user_pkg?.rest_trial_days !== 0) {
      return t("StartInitpackageinfoRestTrialDaysDa", {
        replace: {
          days: user_pkg?.rest_trial_days,
        },
      })
    }

    return isUpgradePlan ? t("Upgrade") : t("Downgrade")
  }, [
    canResumeCurrentPlan,
    isActiveCurrentPlan,
    isNextPlan,
    isSubscribeGuide,
    isUpgradePlan,
    kind,
    user_pkg?.rest_trial_days,
    hasChosenPlanFlag,
  ])

  return (
    <div className={compact ? undefined : "mt-4 mb-2"}>
      <Button
        fullWidth
        variant={
          kind === 1 || (isActiveCurrentPlan && !canResumeCurrentPlan)
            ? undefined
            : "primary"
        }
        loading={loading}
        disabled={btnDisabled}
        size={"large"}
        onClick={() => handPayMend()}
      >
        {btnText}
      </Button>
    </div>
  )
}

export default observer(PlanButton)
